import React, { useLayoutEffect, useMemo, useRef, useState } from "react";

const BYOVoteBarChart = ({ counts, labels, threshold = 0 }) => {
  // Handle empty data gracefully
  const hasData = counts.length > 0;
  const maxCount = hasData ? Math.max(...counts) : 10;
  const minCount = hasData ? Math.min(...counts) : 0;

  // Calculate range for better visualization with some padding
  const padding = (maxCount - minCount) * 0.1 || 1;
  const visualMin = Math.floor(minCount - padding);
  const visualMax = Math.ceil(maxCount + padding);

  // Generate y-axis labels
  const generateYAxisLabels = () => {
    const labels = [];
    const range = visualMax - visualMin;
    const step = range > 10 ? Math.ceil(range / 5) : 1;

    for (let i = visualMin; i <= visualMax; i += step) {
      if (i >= 0) labels.push(i);
    }
    return labels;
  };

  const yAxisLabels = generateYAxisLabels();

  // Measure the container so the SVG always spans the available space.
  const wrapRef = useRef(null);
  const [size, setSize] = useState({ w: 560, h: 260 });
  const [hover, setHover] = useState(null);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () =>
      setSize({ w: Math.max(260, el.clientWidth), h: Math.max(180, el.clientHeight) });
    update();
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(update);
      ro.observe(el);
    } else {
      window.addEventListener('resize', update);
    }
    return () => {
      if (ro) ro.disconnect();
      else window.removeEventListener('resize', update);
    };
  }, []);

  const geom = useMemo(() => {
    const padL = 34;
    const padR = 12;
    const padT = 16;
    const padB = 26;
    const n = counts.length;

    const svgW = size.w;
    const svgH = size.h;
    const innerW = svgW - padL - padR;
    const innerH = svgH - padT - padB;
    const slot = n > 0 ? innerW / n : innerW;
    const barW = Math.min(slot * 0.62, 48);

    const span = visualMax - visualMin || 1;
    const baselineY = padT + innerH;
    const yFor = (v) => baselineY - ((v - visualMin) / span) * innerH;
    const xFor = (i) => padL + slot * i + slot / 2;
    // Mirrors the original percentage heights, including the 5% visibility floor.
    const heightFor = (v) => (Math.max(((v - visualMin) / span) * 100, 5) / 100) * innerH;

    return { padL, padR, padT, padB, svgW, svgH, innerH, slot, barW, baselineY, yFor, xFor, heightFor };
  }, [counts.length, size, visualMin, visualMax]);

  const { padL, padR, padT, padB, svgW, svgH, slot, barW, baselineY, yFor, xFor, heightFor } = geom;
  const showThresholdLine = threshold >= visualMin && threshold <= visualMax;

  // Shorten Betfair to BF when the chart is narrow (mobile widths)
  const getDisplayLabel = (label) => (label === "Betfair" && svgW < 480 ? "BF" : label);

  // Changing this remounts the bars so the grow-in animation replays whenever
  // the selection changes.
  const animKey = `${counts.length}|${counts.join(',')}`;

  return (
    <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-3 w-full h-full flex flex-col min-h-0">
      <style>{`
        @keyframes byoBarGrow { from { transform: scaleY(0.001); } to { transform: scaleY(1); } }
        .byo-bar-grow { transform-box: fill-box; transform-origin: 50% 100%; animation: byoBarGrow 0.5s cubic-bezier(0.22, 1, 0.36, 1) backwards; }
      `}</style>

      <div className="flex items-center justify-between mb-2 flex-shrink-0">
        <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-slate-500">
          Vote Visualization
        </div>
        {hasData && (
          <div className="text-[10px] font-mono tabular-nums text-slate-500">
            line <span className="text-emerald-300 font-semibold">{threshold}</span>
          </div>
        )}
      </div>

      <div ref={wrapRef} className="relative w-full flex-1 min-h-[220px] sm:min-h-[260px] lg:min-h-0">
        {hasData ? (
          <svg width={svgW} height={svgH} className="block" role="img" aria-label="Combined votes by dataset">
            <defs>
              <linearGradient id="byoBarHit" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#34d399" />
                <stop offset="100%" stopColor="#059669" />
              </linearGradient>
              <linearGradient id="byoBarMiss" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#fb7185" />
                <stop offset="100%" stopColor="#e11d48" />
              </linearGradient>
            </defs>

            {/* Gridlines + y labels */}
            {yAxisLabels.map((label) => {
              const y = yFor(label);
              return (
                <g key={`grid-${label}`}>
                  <line x1={padL} y1={y} x2={svgW - padR} y2={y} stroke="#1e293b" strokeWidth="1" />
                  <text
                    x={padL - 6}
                    y={y + 3}
                    textAnchor="end"
                    className="fill-slate-500"
                    style={{ fontSize: 9, fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {label}
                  </text>
                </g>
              );
            })}

            {/* Threshold line */}
            {showThresholdLine && (
              <g>
                <line
                  x1={padL}
                  y1={yFor(threshold)}
                  x2={svgW - padR}
                  y2={yFor(threshold)}
                  stroke="#94a3b8"
                  strokeWidth="1.25"
                  strokeDasharray="2 3"
                  opacity="0.7"
                />
                <text
                  x={padL + 2}
                  y={yFor(threshold) - 4}
                  className="fill-slate-400"
                  style={{ fontSize: 9, fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif', letterSpacing: '0.08em' }}
                >
                  LINE {threshold}
                </text>
              </g>
            )}

            {/* Bars (keyed group so the grow-in animation replays on change) */}
            <g key={animKey}>
              {counts.map((count, index) => {
                const v = Number(count) || 0;
                const x = xFor(index);
                const h = heightFor(v);
                const y = baselineY - h;
                const isAboveLine = count >= threshold;
                const dim = hover != null && hover !== index;
                const growDelay = `${Math.min(index * 40, 500)}ms`;
                const inside = h >= 16;
                return (
                  <g
                    key={`${labels[index]}-${count}-${index}`}
                    onMouseEnter={() => setHover(index)}
                    onMouseLeave={() => setHover((prev) => (prev === index ? null : prev))}
                    style={{ cursor: 'default' }}
                  >
                    {/* Hover hit area */}
                    <rect x={x - slot / 2} y={padT} width={slot} height={svgH - padT - padB} fill="transparent" />
                    <rect
                      x={x - barW / 2}
                      y={y}
                      width={barW}
                      height={h}
                      rx={3}
                      fill={isAboveLine ? 'url(#byoBarHit)' : 'url(#byoBarMiss)'}
                      opacity={dim ? 0.45 : 1}
                      className="byo-bar-grow"
                      style={{ transition: 'opacity 0.12s, fill 0.3s', animationDelay: growDelay }}
                    >
                      <title>{`${labels[index]}: ${v.toFixed(2)}`}</title>
                    </rect>
                    {/* Value label — inside the bar when tall enough, otherwise above it */}
                    <text
                      x={x}
                      y={inside ? y + 12 : y - 4}
                      textAnchor="middle"
                      className={inside ? 'fill-slate-950' : 'fill-slate-200'}
                      style={{
                        fontSize: 9,
                        fontWeight: 700,
                        fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
                        fontVariantNumeric: 'tabular-nums',
                        pointerEvents: 'none',
                        ...(inside
                          ? { paintOrder: 'stroke', stroke: 'rgba(255,255,255,0.35)', strokeWidth: 2, strokeLinejoin: 'round' }
                          : null),
                      }}
                      opacity={dim ? 0.5 : 1}
                    >
                      {v.toFixed(2)}
                    </text>
                    {/* Dataset label */}
                    <text
                      x={x}
                      y={svgH - 8}
                      textAnchor="middle"
                      className={hover === index ? 'fill-slate-300' : 'fill-slate-500'}
                      style={{
                        fontSize: 9,
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
                      }}
                    >
                      {getDisplayLabel(labels[index])}
                    </text>
                  </g>
                );
              })}
            </g>

            {/* Baseline */}
            <line x1={padL} y1={baselineY} x2={svgW - padR} y2={baselineY} stroke="#334155" strokeWidth="1.25" />
          </svg>
        ) : (
          // Empty state: show placeholder text in center of chart
          <div className="absolute inset-0 flex items-center justify-center rounded-lg border border-dashed border-slate-800/80 bg-[#0a1120]/40">
            <div className="text-slate-500 text-xs font-mono text-center px-2">
              <div>No data to display</div>
              <div className="text-[10px] mt-1 text-slate-600 uppercase tracking-[0.12em]">Select players to view vote data</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BYOVoteBarChart;
